import cx from "classnames";
import { useMemo } from "react";
import { FullfillmentType, OrderPage_GetExistingOrderQuery } from "../../../codegen/graphql";
import { getPlatformColor } from "../../utils/getPlatformColor";
import dayjs from "dayjs";
import Button from "../common/Button";
import FulfillmentStatus from "../common/FulfillmentStatus";
import { getTranslatedValue, Translations } from "../common/TranslatedValue";


export default function OrderPageFulfillmentRow({
  orderFulfillment,
  onEdit,
  onDelete,
  translationLang,
}: {
  orderFulfillment: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderFulfillments'][0];
  onEdit?: (fulfillment: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderFulfillments'][0]) => void;
  onDelete?: (fulfillment: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderFulfillments'][0]) => void;
  translationLang?: string;
}) {

  const externalOrderLink = useMemo(() => {
    if (orderFulfillment.fullfilledPlatform === 'aliexpress') {
      return `https://www.aliexpress.com/p/order/detail.html?orderId=${orderFulfillment.fullfilledOrderId}`;
    }
    else if (orderFulfillment.fullfilledPlatform === 'leboncoin') {
      return `https://www.leboncoin.fr/compte/pro/commandes`;
    }
    else if (orderFulfillment.fullfilledPlatform === 'sendcloud') {
      if (orderFulfillment.type === FullfillmentType.Outward) {
        return `https://app.sendcloud.com/v2/shipping/${orderFulfillment.fullfilledOrderId}/details`
      } else {
        return `https://app.sendcloud.com/v2/returns/${orderFulfillment.fullfilledOrderId}`
      }
    }
    else if (orderFulfillment.fullfilledPlatform === 'ebay_packlink') {
      return `https://expeditions.ebay.fr/private/shipments/${orderFulfillment.fullfilledOrderId}/details`
    }
  }, [orderFulfillment.fullfilledPlatform, orderFulfillment.fullfilledOrderId]);

  return (
    <div key={orderFulfillment.id} className="border border-gray-200 rounded-md p-4 mb-2 bg-white">
      <div className="grid grid-cols-12">
        <div className="col-span-3">
          <div
            className={cx(
              getPlatformColor(orderFulfillment.fullfilledPlatform || '').textClass,
            )}
          >
            <div className={`text-md font-medium capitalize`}>
              {orderFulfillment.fullfilledPlatform || 'N/A'}
            </div>
            <a href={externalOrderLink} target="_blank" rel="noopener noreferrer" className="underline block text-lg">
              Order ID: {orderFulfillment.fullfilledOrderId}
            </a>
          </div>
          <div className="text-md mb-2">
            Created: {dayjs(orderFulfillment.createdAt).format('DD ddd. MMMM, HH:mm')}
          </div>
          
          <div className="text-md mb-2">
            <FulfillmentStatus status={orderFulfillment.fullfilledStatus} />
          </div>

          {orderFulfillment.shippingCarrier && (<>
            <div className="text-md mb-2">
              <a href={`https://global.cainiao.com/newDetail.htm?mailNoList=${orderFulfillment.trackingNumber}`} target="_blank" rel="noopener noreferrer" className="underline">
                Shipping: {orderFulfillment.shippingCarrier}<br/>
                Tracking number: {orderFulfillment.trackingNumber}
              </a>
            </div>
          </>)}

        </div>

        <div className="col-span-2">
          Packaging: {orderFulfillment.packaging?.weight} kg<br/>
          Dimensions: {orderFulfillment.packaging?.size1} x {orderFulfillment.packaging?.size2} x {orderFulfillment.packaging?.size3} cm
        </div>
        
        <div className="col-span-6">
          {orderFulfillment.orderProductsVariations.map(orderProductsVariation => (
            <div key={orderProductsVariation.id} className="flex items-center space-x-2">
              <img src={orderProductsVariation.adVariation?.mainImage?.url} className="w-10 h-10 rounded-md" />
              <p>{getTranslatedValue(orderProductsVariation.adVariation?.ad.title as unknown as Translations, translationLang)}</p>
              <p>x {orderProductsVariation.quantity}</p>
            </div>
          ))}
        </div>

        <div className="col-span-1">
            {onEdit && (
              <Button
                preset="white"
                onClick={() => onEdit(orderFulfillment)}
              >
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                preset="danger-light"
                onClick={() => onDelete(orderFulfillment)}
              >
                Delete
              </Button>
            )}
        </div>
      </div>
    </div>
  );
}